import React, { useState, useEffect, useCallback } from "react";
import styles from "./EditCustomerModal.module.scss";
import classNames from "classnames/bind";
import { notify } from "~/utils/helper";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { BaseAddressInput } from "~/components/common";
import { ICustomer } from "~/utils/interface/jobDetails";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { AddressSuggestion } from "~/pages/admin/jobs/add-job/types/add-job.types";
import { googlePlacesApiKey } from "~/utils/constants/env";

const cx = classNames.bind(styles);

interface EditCustomerModalProps {
  isOpen: boolean;
  customerDetail?: ICustomer;
  onClose: () => void;
  onSave: (data: any) => void;
  mode?: "add" | "edit";
}

const REFERRED_BY_OPTIONS = [
  { label: "Google Search", value: "GOOGLE_SEARCH" },
  { label: "Referral", value: "REFERRAL" },
];

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({
  isOpen,
  customerDetail,
  onClose,
  onSave,
  mode = "edit",
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    referredBy: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const [selectedSuggestion, setSelectedSuggestion] =
    useState<AddressSuggestion | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (mode === "edit" && customerDetail && isOpen) {
      setFormData({
        name: customerDetail.name || "",
        email: customerDetail.primaryEmail || "",
        phone: customerDetail.primaryPhone || "",
        referredBy: customerDetail.referredBy || "",
        address: customerDetail.addressLine1 || "",
        city: customerDetail.city || "",
        state: customerDetail.state || "",
        zip: customerDetail.postalCode || customerDetail.zipCodes || "",
        country: customerDetail.country || "",
      });
      setErrors({});
      setSelectedSuggestion(null);
    } else if (mode === "add" && isOpen) {
      // Reset form for Add mode
      setFormData({
        name: "",
        email: "",
        phone: "",
        referredBy: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
      });
      setErrors({});
      setSelectedSuggestion(null);
    }
  }, [customerDetail, isOpen, mode]);

  const handleAddressChange = useCallback((value: string) => {
    setFormData((prev) => {
      if (prev.address === value) return prev;
      return { ...prev, address: value };
    });

    setErrors((prev) => {
      if (!prev.address) return prev;
      return { ...prev, address: "" };
    });
  }, []);

  const handleAddressSuggestionSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      setSelectedSuggestion(suggestion);
      const newAddress = suggestion?.placePrediction?.text?.text || "";
      setFormData((prev) => ({ ...prev, address: newAddress }));
    },
    []
  );

  const getCoordinatesFromAddress = async (): Promise<{
    latitude: number;
    longitude: number;
  }> => {
    if (mode === "edit" && formData.address === customerDetail?.addressLine1) {
      return { latitude: 0, longitude: 0 };
    }

    if (!selectedSuggestion) {
      return { latitude: 0, longitude: 0 };
    }

    const placeId = selectedSuggestion.placePrediction?.placeId;
    if (!placeId) {
      return { latitude: 0, longitude: 0 };
    }

    try {
      const res = await fetch(
        `https://places.googleapis.com/v1/places/${placeId}`,
        {
          method: "GET",
          headers: {
            "X-Goog-Api-Key":
              googlePlacesApiKey || "AIzaSyBFvJr6l0XfUv2axQAvHaHnTzwzwlKJ1oo",
            "X-Goog-FieldMask": "location",
          },
        }
      );

      const data = await res.json();
      const location = data?.location;

      if (location?.latitude && location?.longitude) {
        return {
          latitude: location.latitude,
          longitude: location.longitude,
        };
      }
    } catch (err) {
      console.error("Failed to get place detail:", err);
    }

    return { latitude: 0, longitude: 0 };
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d+$/.test(formData.phone)) {
      newErrors.phone = "Phone must be numeric";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!validateForm()) {
      notify("error", "Please fix all validation errors before saving");
      return;
    }

    const { latitude, longitude } = await getCoordinatesFromAddress();

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phone.trim(),
      address: formData.address.trim(),
      zipCode: formData.zip.trim(),
      latitude: latitude,
      longitude: longitude,
      referredBy: formData.referredBy,
      city: formData.city.trim(),
      state: formData.state.trim(),
      country: formData.country.trim(),
    };

    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <BaseModalV2
      open={isOpen}
      onClose={onClose}
      title={mode === "add" ? "Add Contact" : "Edit Contact"}
    >
      <div className={cx("edit-customer-container")}>
        <div className={cx("body")}>
          <div className={cx("row")}>
            <PrimaryInput
              name="name"
              label="Name"
              placeholder="Enter name"
              value={formData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              errorMessage={errors.name}
              className={cx("input", { error: errors.name })}
            />

            <PrimaryInput
              name="email"
              label="Email"
              placeholder="Enter email"
              value={formData.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              errorMessage={errors.email}
              className={cx("input", { error: errors.email })}
              disabled={mode === "edit"}
            />
          </div>

          <div className={cx("row")}>
            <PrimaryInput
              name="phone"
              label="Phone"
              placeholder="Enter phone"
              value={formData.phone}
              onChange={(e) => handleFieldChange("phone", e.target.value)}
              errorMessage={errors.phone}
              className={cx("input", { error: errors.phone })}
            />

            <PrimarySelectInput
              name="referredBy"
              label="Referred by"
              placeholder="Select referred by"
              value={formData.referredBy}
              onChange={(value) => handleFieldChange("referredBy", value)}
              errorMessage={errors.referredBy}
              className={cx("select-input", { error: errors.referredBy })}
              options={REFERRED_BY_OPTIONS.map((option) => ({
                label: option.label,
                value: option.label,
              }))}
              classNameIcon={cx("select-input-icon")}
            />
          </div>

          <div className={cx("row")}>
            <BaseAddressInput
              value={formData.address}
              onChange={handleAddressChange}
              placeholder="Enter full address"
              label="Billing Address"
              errorMessage={errors.address}
              inputClassName={cx("address-input", { error: errors.address })}
              className={cx("address-input-container")}
              onSuggestionSelect={handleAddressSuggestionSelect}
            />
          </div>

          <div className={cx("row")}>
            <PrimaryInput
              name="city"
              label="City"
              placeholder="Enter city"
              value={formData.city}
              onChange={(e) => handleFieldChange("city", e.target.value)}
              errorMessage={errors.city}
              className={cx("input", { error: errors.city })}
            />

            <PrimaryInput
              name="state"
              label="State"
              placeholder="Enter state"
              value={formData.state}
              onChange={(e) => handleFieldChange("state", e.target.value)}
              errorMessage={errors.state}
              className={cx("input", { error: errors.state })}
            />
          </div>

          <div className={cx("row")}>
            <PrimaryInput
              name="zip"
              label="Zip"
              placeholder="Enter zip"
              value={formData.zip}
              onChange={(e) => handleFieldChange("zip", e.target.value)}
              errorMessage={errors.zip}
              className={cx("input", { error: errors.zip })}
            />

            <PrimaryInput
              name="country"
              label="Country"
              placeholder="Enter country"
              value={formData.country}
              onChange={(e) => handleFieldChange("country", e.target.value)}
              errorMessage={errors.country}
              className={cx("input", { error: errors.country })}
            />
          </div>
        </div>

        <div className={cx("footer")}>
          <PrimaryButton
            title="Cancel"
            onClick={handleCancel}
            className={cx("cancel-button")}
          />

          <PrimaryButton
            title={mode === "add" ? "Add Contact" : "Save Changes"}
            onClick={handleSave}
            className={cx("save-button")}
          />
        </div>
      </div>
    </BaseModalV2>
  );
};

export default EditCustomerModal;
